import { Checkbox, DatePicker, Divider, Form, Input, Select, Space, Tooltip, Typography } from "antd";
import {
  InfoCircleOutlined,
  WalletOutlined,
  CrownOutlined,
} from "@ant-design/icons";
import { useMemo, useEffect } from "react";
import { useTranslation } from "react-i18next";
import type { BookingState } from "../../../contexts/BookingWizardContext";
import { isMissingField } from "../utils/mapAnalysisToBooking";
import { CompletionProgressBar } from "./CompletionProgressBar";
import { StatusFormField } from "./StatusFormField";
import { SavedSupplierPicker, type SupplierFormValues } from "./SavedSupplierPicker";
import { ProductEditorTable } from "./ProductEditorTable";
import { fieldKeyToDomId } from "../utils/fieldLabels";
import { createSmartDetailsFormRules } from "../utils/smartDetailsFormRules";
import type { CompanySupplier } from "../../../hooks/useCompanySuppliers";
import {
  isFclBookingType,
  SERVICE_LEVEL_VALUES,
} from "../../../utils/serviceLevelUtils";
import { ChoiceCardGroup } from "../../BookingWizard/ChoiceCardGroup";
import {
  ShippingContainerIcon,
  LclBoxesIcon,
} from "../../BookingWizard/ShippingContainerIcon";
import { CargoShipIcon, PlaneIcon } from "../../BookingWizard/TransportModeIcons";

const { Title } = Typography;

type Product = NonNullable<BookingState["products"]>[number];

const bookingTypeIcon = (name: string) => {
  const key = name.trim().toUpperCase();
  if (key.includes("FCL")) return <ShippingContainerIcon />;
  return <LclBoxesIcon />;
};

const transportTypeIcon = (abbreviation: string | undefined, name: string) => {
  const key = `${abbreviation ?? ""} ${name}`.toUpperCase();
  if (key.includes("AIR")) return <PlaneIcon />;
  return <CargoShipIcon />;
};

export interface SmartDetailsFormProps {
  missingFields: string[];
  isSeafreight: boolean;
  products: Product[];
  loadingTransport: boolean;
  loadingBookingTypes: boolean;
  loadingIncoterms: boolean;
  transportTypes: { id: number; name: string; abbreviation?: string }[];
  bookingTypes: { id: number; name: string }[];
  incoterms: { id: number; name: string }[];
  onTransportChange: (value: number) => void;
  onSupplierSelect: (values: SupplierFormValues, supplier: CompanySupplier | null) => void;
  onAddProduct: (product: Product) => void;
  onProductsChange: (products: Product[]) => void;
  fieldStatus: (key: string) => "warning" | undefined;
}

export const SmartDetailsForm = ({
  missingFields,
  isSeafreight,
  products,
  loadingTransport,
  loadingBookingTypes,
  loadingIncoterms,
  transportTypes,
  bookingTypes,
  incoterms,
  onTransportChange,
  onSupplierSelect,
  onAddProduct,
  onProductsChange,
  fieldStatus,
}: SmartDetailsFormProps) => {
  const { t } = useTranslation();
  const form = Form.useFormInstance();
  const rules = createSmartDetailsFormRules(t);
  const watchedBookingType = Form.useWatch("booking_type", form);
  const isFcl = isSeafreight && isFclBookingType(watchedBookingType);

  const showSupplierPicker = isMissingField(missingFields, "supplier.name");

  const transportTypeOptions = useMemo(
    () =>
      transportTypes.map((type) => ({
        value: type.id,
        label: type.name,
        icon: transportTypeIcon(type.abbreviation, type.name),
      })),
    [transportTypes]
  );

  const bookingTypeOptions = useMemo(
    () =>
      bookingTypes.map((type) => ({
        value: type.id,
        label: type.name,
        icon: bookingTypeIcon(type.name),
      })),
    [bookingTypes]
  );

  const serviceLevelOptions = useMemo(
    () =>
      SERVICE_LEVEL_VALUES.map((value) => ({
        value,
        label: t(
          `bookingWizard.transportDetails.serviceLevel${value === "budget" ? "Budget" : "Premium"}`
        ),
        description: t(
          `bookingWizard.transportDetails.serviceLevel${value === "budget" ? "Budget" : "Premium"}Hint`
        ),
        icon: value === "budget" ? <WalletOutlined /> : <CrownOutlined />,
      })),
    [t]
  );

  useEffect(() => {
    if (
      watchedBookingType != null &&
      !isFclBookingType(watchedBookingType)
    ) {
      form.setFieldValue("service_level", undefined);
    }
  }, [watchedBookingType, form]);

  return (
    <>
      <CompletionProgressBar
        missingFields={missingFields}
        isSeafreight={isSeafreight}
        isFcl={isFcl}
        productCount={products.length}
      />

      <Title level={5} style={{ marginTop: 0 }}>
        {t("bookingWizard.steps.transportDetails")}
      </Title>

      <StatusFormField
        fieldKey="transport.transport_type"
        missingFields={missingFields}
        label={t("bookingWizard.transportDetails.transportType")}
        name="transport_type"
        rules={rules.transportType}
      >
        <ChoiceCardGroup
          disabled={loadingTransport}
          options={transportTypeOptions}
          onSelect={onTransportChange}
        />
      </StatusFormField>

      {isSeafreight && (
        <StatusFormField
          fieldKey="transport.booking_type"
          missingFields={missingFields}
          label={t("bookingWizard.transportDetails.bookingType")}
          name="booking_type"
          rules={rules.bookingType}
        >
          <ChoiceCardGroup
            disabled={loadingBookingTypes}
            options={bookingTypeOptions}
          />
        </StatusFormField>
      )}

      {isFcl && (
        <StatusFormField
          fieldKey="transport.service_level"
          missingFields={missingFields}
          label={
            <Space size={6}>
              {t("bookingWizard.transportDetails.serviceLevel")}
              <Tooltip title={t("bookingWizard.transportDetails.serviceLevelInfo")}>
                <InfoCircleOutlined style={{ color: "#1890ff" }} />
              </Tooltip>
            </Space>
          }
          name="service_level"
          rules={rules.serviceLevel}
        >
          <ChoiceCardGroup options={serviceLevelOptions} />
        </StatusFormField>
      )}

      <StatusFormField
        fieldKey="transport.booking_incoterm"
        missingFields={missingFields}
        label={t("bookingWizard.transportDetails.incoterm")}
        name="booking_incoterm"
        rules={rules.bookingIncoterm}
      >
        <Select
          loading={loadingIncoterms}
          status={fieldStatus("transport.booking_incoterm")}
          options={incoterms.map((i) => ({ value: i.id, label: i.name }))}
          style={{ width: "100%" }}
        />
      </StatusFormField>

      <StatusFormField
        fieldKey="transport.origin_pickup_date"
        missingFields={missingFields}
        label={t("bookingWizard.transportDetails.cargoReadinessDate")}
        name="origin_pickup_date"
        rules={rules.originPickupDate}
      >
        <DatePicker
          style={{ width: "100%" }}
          status={fieldStatus("transport.origin_pickup_date")}
        />
      </StatusFormField>

      <Divider />

      <Title level={5}>{t("bookingWizard.steps.supplierDetails")}</Title>

      {showSupplierPicker && (
        <div style={{ marginBottom: 16 }}>
          <SavedSupplierPicker onSelect={onSupplierSelect} />
        </div>
      )}

      <StatusFormField
        fieldKey="supplier.name"
        missingFields={missingFields}
        label={t("bookingWizard.supplierDetails.supplierName")}
        name="supplier_name"
        rules={rules.supplierName}
      >
        <Input status={fieldStatus("supplier.name")} />
      </StatusFormField>

      <StatusFormField
        fieldKey="supplier.email"
        missingFields={missingFields}
        label={t("bookingWizard.supplierDetails.email")}
        name="supplier_email"
        rules={rules.supplierEmail}
      >
        <Input status={fieldStatus("supplier.email")} type="email" autoComplete="email" />
      </StatusFormField>

      <StatusFormField
        fieldKey="supplier.phone"
        missingFields={missingFields}
        label={t("bookingWizard.supplierDetails.phone")}
        name="supplier_phone"
        rules={rules.supplierPhone}
      >
        <Input status={fieldStatus("supplier.phone")} />
      </StatusFormField>

      <Form.Item label={t("bookingWizard.supplierDetails.wechat")} name="supplier_wechat">
        <Input />
      </Form.Item>

      <StatusFormField
        fieldKey="supplier.country"
        missingFields={missingFields}
        label={t("bookingWizard.supplierDetails.country")}
        name="supplier_country"
        rules={rules.supplierCountry}
      >
        <Input status={fieldStatus("supplier.country")} />
      </StatusFormField>

      <StatusFormField
        fieldKey="supplier.province"
        missingFields={missingFields}
        label={t("bookingWizard.supplierDetails.province")}
        name="supplier_province"
        rules={rules.supplierProvince}
      >
        <Input status={fieldStatus("supplier.province")} />
      </StatusFormField>

      <StatusFormField
        fieldKey="supplier.city"
        missingFields={missingFields}
        label={t("bookingWizard.supplierDetails.city")}
        name="supplier_city"
        rules={rules.supplierCity}
      >
        <Input status={fieldStatus("supplier.city")} />
      </StatusFormField>

      <StatusFormField
        fieldKey="supplier.address"
        missingFields={missingFields}
        label={t("bookingWizard.supplierDetails.address")}
        name="supplier_address"
        rules={rules.supplierAddress}
      >
        <Input.TextArea rows={2} status={fieldStatus("supplier.address")} />
      </StatusFormField>

      <Form.Item name="saveSupplierForLater" valuePropName="checked">
        <Checkbox>{t("bookingWizard.supplierDetails.saveForLater")}</Checkbox>
      </Form.Item>

      <Divider />

      <Title level={5}>{t("bookingWizard.steps.productDetails")}</Title>

      <div id={fieldKeyToDomId("products")} style={{ scrollMarginTop: 96 }}>
        <ProductEditorTable
          products={products}
          missingFields={missingFields}
          onChange={onProductsChange}
          onAddProduct={onAddProduct}
        />
      </div>

      <Divider />

      <Title level={5}>{t("bookingWizard.steps.basicDetails")}</Title>

      <Form.Item label={t("bookingWizard.basicDetails.reference")} name="reference">
        <Input placeholder={t("bookingWizard.basicDetails.reference")} />
      </Form.Item>
    </>
  );
};
